import type {
  CustomNode,
  MapBodySlot,
  MapExecutionConfig,
  NodeStruct,
} from '@/types/node-types';
import { ConnectionType, type CustomEdge } from '@/types/edge-types';
import {
  getMapConfig,
  isMapBodyEligible,
  isMapNode,
  MAP_NODE_ID,
} from './map-control';
import {
  isRepeatAttemptEligible,
  isRepeatJudgeEligible,
} from './repeat-control';
import {
  catalogStructFor,
  edgeTargetSlot,
  nodeLabel,
  topologicalLevels,
  type LoopRuleProblem,
} from './repeat-loop';
import {
  branchEdgesLeavingRegion,
  checkBranchesRejoin,
  isGatingNode,
} from './region-branches';

/**
 * Phase 8c: a Map whose body is a visible part of the canvas ("For each").
 *
 *   Map ──(each item)──▶ first step ──…──▶ last step ──(collect, dashed)──▶ Map
 *
 * The item edge leaves the Map's `map:item` handle and lands on the first
 * step's item slot; the collector edge (`edge_type: map_collect`, target
 * handle `map:collect`) closes the body. The body is every node on a forward
 * path from the first step to the last. With a collector the backend
 * compiles the body into config `1.1` (`region`); without one the Map keeps
 * today's single Map Item call (`1.0`). This module reads the body off the
 * graph and checks the prepare rules live, in plain words.
 */

export const MAP_ITEM_SOURCE_HANDLE = 'map:item';
export const MAP_COLLECT_TARGET_HANDLE = 'map:collect';
export const MAP_ITEM_TARGET_HANDLE_PREFIX = 'map:item:';
export const MAP_ITEM_ROLE = 'map_item';
export const MAP_COLLECT_ROLE = 'map_collect';
export const MAP_REGION_VERSION = '1.1';
export const DEFAULT_MAP_HISTORY_EVENTS = 2_500;

const EXECUTION_EDGE_TYPES = new Set<string>([
  ConnectionType.PORT_TO_PORT,
  ConnectionType.PORT_TO_PARAM,
  ConnectionType.CONDITIONAL,
]);
const NON_EXECUTABLE_NODE_TYPES = new Set(['tool', 'memory', 'annotation']);

const struct = (node: CustomNode | undefined): NodeStruct | undefined =>
  node?.data?.struct;
const edgeSource = (edge: CustomEdge): string =>
  edge.source_node_id || edge.source;
const edgeTarget = (edge: CustomEdge): string =>
  edge.target_node_id || edge.target;
const role = (edge: CustomEdge): string | undefined =>
  (edge.data as { map_role?: string } | undefined)?.map_role;

export const mapItemTargetHandleId = (slot: MapBodySlot): string =>
  `${MAP_ITEM_TARGET_HANDLE_PREFIX}${slot.kind}:${slot.target_id}`;

export const parseMapItemTargetHandle = (
  handle: string | null | undefined,
): MapBodySlot | null => {
  if (!handle?.startsWith(MAP_ITEM_TARGET_HANDLE_PREFIX)) return null;
  const [kind, ...rest] = handle
    .slice(MAP_ITEM_TARGET_HANDLE_PREFIX.length)
    .split(':');
  const targetId = rest.join(':');
  if ((kind !== 'input' && kind !== 'param') || !targetId) return null;
  return { kind, target_id: targetId };
};

export const isMapCollectEdge = (edge: CustomEdge): boolean =>
  edge.edge_type === ConnectionType.MAP_COLLECT ||
  edge.targetHandle === MAP_COLLECT_TARGET_HANDLE ||
  role(edge) === MAP_COLLECT_ROLE;

export const isMapItemEdge = (edge: CustomEdge): boolean =>
  edge.sourceHandle === MAP_ITEM_SOURCE_HANDLE ||
  edge.source_port_id === MAP_ITEM_SOURCE_HANDLE ||
  role(edge) === MAP_ITEM_ROLE;

/** Either For-each edge: drawn and validated by this module, not as data edges. */
export const isForEachEdge = (edge: CustomEdge): boolean =>
  isMapCollectEdge(edge) || isMapItemEdge(edge);

// --- eligibility -------------------------------------------------------------

/** Can this node run inside a For-each body (any position)? */
export const canBeInForEach = (
  s: NodeStruct | undefined,
  catalog?: readonly NodeStruct[],
): boolean => {
  const effective = catalogStructFor(s, catalog);
  if (!effective || effective.node_id === MAP_NODE_ID) return false;
  return (
    isMapBodyEligible(effective) ||
    isRepeatAttemptEligible(effective) ||
    isRepeatJudgeEligible(effective)
  );
};

/**
 * Where the item lands on a first step: the Map body contract's item slot,
 * else the repeat contract's first input slot (Lambda `input_data`, Agent
 * `user_prompt`). Null when the node cannot take an item.
 */
export const forEachItemSlot = (
  s: NodeStruct | undefined,
  catalog?: readonly NodeStruct[],
): MapBodySlot | null => {
  const effective = catalogStructFor(s, catalog);
  if (!effective || !canBeInForEach(effective, catalog)) return null;
  if (isBranchingNodeId(effective.node_id)) return null;
  if (isMapBodyEligible(effective)) {
    const slot = effective.map_body_contract?.item_slots?.[0];
    if (slot) return slot;
  }
  return effective.repeat_body_contract?.input_slots?.[0] ?? null;
};

const isBranchingNodeId = (nodeId: string | undefined) =>
  nodeId === 'switch_node' || nodeId === 'if_node';

/**
 * Does this runtime's Map accept a region body? True when the catalog's
 * `config_schema.properties.version` allows `1.1` (const or enum).
 */
export const mapSupportsRegion = (map: NodeStruct | undefined): boolean => {
  const version = (
    map?.config_schema as
      | { properties?: { version?: { const?: unknown; enum?: unknown } } }
      | undefined
  )?.properties?.version;
  if (!version) return false;
  if (version.const === MAP_REGION_VERSION) return true;
  return (
    Array.isArray(version.enum) && version.enum.includes(MAP_REGION_VERSION)
  );
};

/** Every map-safe node name, for "nodes that can …" hints. */
const forEachAlternatives = (catalog: readonly NodeStruct[] | undefined) => {
  const names = [
    ...new Set(
      (catalog ?? [])
        .filter((entry) => canBeInForEach(entry, catalog))
        .map((entry) => entry.display_name)
        .filter(Boolean),
    ),
  ].sort();
  return names.length ? `Nodes that can: ${names.join(', ')}.` : '';
};

// --- the body, from the graph ------------------------------------------------

export interface ForEachRegion {
  carrierId: string;
  carrier: CustomNode;
  /** A collector edge exists: the Map runs a visible body (config 1.1). */
  regionMode: boolean;
  /** Where each item goes. */
  entryId: string | null;
  /** The step whose output is collected. */
  exitId: string | null;
  /** First step to last step, inclusive, sorted. */
  regionIds: string[];
  /** Body nodes in run order (topological levels flattened). */
  orderedIds: string[];
  itemEdges: CustomEdge[];
  collectEdges: CustomEdge[];
  itemSlot: MapBodySlot | null;
  /** The output port of the last step that is collected. */
  collectPortId: string | null;
}

const isExecutionNode = (node: CustomNode): boolean => {
  const s = struct(node);
  return Boolean(s) && !NON_EXECUTABLE_NODE_TYPES.has(String(s!.node_type));
};

const ordinaryEdges = (
  edges: readonly CustomEdge[],
  executionIds: ReadonlySet<string>,
) =>
  edges.filter(
    (edge) =>
      !isForEachEdge(edge) &&
      EXECUTION_EDGE_TYPES.has(String(edge.edge_type)) &&
      executionIds.has(edgeSource(edge)) &&
      executionIds.has(edgeTarget(edge)),
  );

const reach = (
  start: string,
  edges: readonly CustomEdge[],
  forward: boolean,
  blocked: string,
): Set<string> => {
  const seen = new Set<string>();
  const queue = [start];
  while (queue.length) {
    const current = queue.shift()!;
    if (seen.has(current)) continue;
    seen.add(current);
    for (const edge of edges) {
      const from = forward ? edgeSource(edge) : edgeTarget(edge);
      const to = forward ? edgeTarget(edge) : edgeSource(edge);
      if (from === current && to !== blocked && !seen.has(to)) queue.push(to);
    }
  }
  return seen;
};

/** Nodes reachable forward from `start` over ordinary edges, not through the Map. */
export const downstreamOf = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  start: string,
  carrierId: string,
): Set<string> => {
  const executionIds = new Set(
    nodes.filter(isExecutionNode).map((node) => node.id),
  );
  return reach(start, ordinaryEdges(edges, executionIds), true, carrierId);
};

export const describeForEach = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  carrierId: string,
): ForEachRegion | null => {
  const carrier = nodes.find((node) => node.id === carrierId);
  if (!carrier || !isMapNode(struct(carrier))) return null;
  const itemEdges = edges.filter(
    (edge) => isMapItemEdge(edge) && edgeSource(edge) === carrierId,
  );
  const collectEdges = edges.filter(
    (edge) => isMapCollectEdge(edge) && edgeTarget(edge) === carrierId,
  );
  const entryId = itemEdges[0] ? edgeTarget(itemEdges[0]) : null;
  const exitId = collectEdges[0] ? edgeSource(collectEdges[0]) : null;
  let regionIds: string[] = [];
  let orderedIds: string[] = [];
  if (entryId && exitId && entryId !== carrierId && exitId !== carrierId) {
    const executionIds = new Set(
      nodes.filter(isExecutionNode).map((node) => node.id),
    );
    const ordinary = ordinaryEdges(edges, executionIds);
    const descendants = reach(entryId, ordinary, true, carrierId);
    const ancestors = reach(exitId, ordinary, false, carrierId);
    regionIds = [...descendants]
      .filter((id) => ancestors.has(id) && id !== carrierId)
      .sort();
    orderedIds = (topologicalLevels(new Set(regionIds), edges) ?? []).flat();
  }
  const entry = entryId ? nodes.find((node) => node.id === entryId) : undefined;
  const itemSlot = itemEdges[0]
    ? (parseMapItemTargetHandle(itemEdges[0].targetHandle) ??
      edgeTargetSlot(itemEdges[0], entry))
    : null;
  return {
    carrierId,
    carrier,
    regionMode: collectEdges.length > 0,
    entryId,
    exitId,
    regionIds,
    orderedIds,
    itemEdges,
    collectEdges,
    itemSlot,
    collectPortId: collectEdges[0]?.sourceHandle ?? null,
  };
};

export const describeForEachRegions = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
): ForEachRegion[] =>
  nodes
    .filter((node) => isMapNode(struct(node)))
    .map((node) => describeForEach(nodes, edges, node.id))
    .filter((region): region is ForEachRegion => region !== null);

// Every canvas node asks "which For each am I in?" on each render.
let regionsCache: {
  nodes: readonly CustomNode[];
  edges: readonly CustomEdge[];
  regions: ForEachRegion[];
} | null = null;

export const describeForEachRegionsCached = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
): ForEachRegion[] => {
  if (
    regionsCache &&
    regionsCache.nodes === nodes &&
    regionsCache.edges === edges
  ) {
    return regionsCache.regions;
  }
  const regions = describeForEachRegions(nodes, edges);
  regionsCache = { nodes, edges, regions };
  return regions;
};

export type ForEachRole = 'entry' | 'exit' | 'only' | 'step';

export const forEachContaining = (
  regions: readonly ForEachRegion[],
  nodeId: string,
): ForEachRegion | null =>
  regions.find(
    (region) => region.regionMode && region.regionIds.includes(nodeId),
  ) ?? null;

export const forEachRole = (
  region: ForEachRegion,
  nodeId: string,
): ForEachRole | null => {
  if (!region.regionIds.includes(nodeId)) return null;
  if (nodeId === region.entryId && nodeId === region.exitId) return 'only';
  if (nodeId === region.entryId) return 'entry';
  if (nodeId === region.exitId) return 'exit';
  return 'step';
};

// --- history budget ----------------------------------------------------------

/** Events one run takes: 32 + items × (body nodes × 3 + 3) (the prepare formula). */
export const historyEventsFor = (items: number, bodyNodes: number): number =>
  32 + items * (Math.max(1, bodyNodes) * 3 + 3);

/** The most items a body of this size fits under the history budget. */
export const maxItemsForBody = (
  bodyNodes: number,
  budget: number = DEFAULT_MAP_HISTORY_EVENTS,
  itemCeiling = 256,
): number =>
  Math.max(
    0,
    Math.min(
      itemCeiling,
      Math.floor((budget - 32) / (Math.max(1, bodyNodes) * 3 + 3)),
    ),
  );

export interface HistoryBudget {
  bodyNodes: number;
  budget: number;
  fits: number;
  sentence: string;
}

export const historyBudgetFor = (
  region: ForEachRegion,
  carrier: NodeStruct | undefined,
): HistoryBudget => {
  const bodyNodes = Math.max(1, region.regionIds.length);
  const budget =
    carrier?.map_runtime_limits?.max_history_events ??
    DEFAULT_MAP_HISTORY_EVENTS;
  const ceiling = carrier?.map_runtime_limits?.max_items ?? 256;
  const fits = maxItemsForBody(bodyNodes, budget, ceiling);
  return {
    bodyNodes,
    budget,
    fits,
    sentence: `With ${bodyNodes} ${
      bodyNodes === 1 ? 'step' : 'steps'
    } per item, one run fits up to ${fits} items.`,
  };
};

// --- live rule checks --------------------------------------------------------

const problem = (
  code: string,
  message: string,
  nodeIds: string[] = [],
  hint?: string,
  severity: LoopRuleProblem['severity'] = 'error',
): LoopRuleProblem => ({ code, severity, message, hint, nodeIds });

/**
 * Check one For-each body against the prepare rules, in plain words. Codes
 * follow the backend's `map_*` region codes. Returns nothing for a Map that
 * has neither an item edge nor a collector (today's single-call Map).
 */
export const checkForEachRules = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  region: ForEachRegion,
  catalog?: readonly NodeStruct[],
): LoopRuleProblem[] => {
  const problems: LoopRuleProblem[] = [];
  if (!region.regionMode && region.itemEdges.length === 0) return problems;
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const name = (id: string | null) => nodeLabel(id ? byId.get(id) : undefined);
  const carrierName = name(region.carrierId);

  if (!region.regionMode) {
    problems.push(
      problem(
        'map_collect_edge_missing',
        `${carrierName} sends each item to ${name(region.entryId)} but collects nothing back.`,
        [region.carrierId],
        'Pick the step whose output is collected in the panel, or drag from that step\u2019s output to the Map\u2019s "collect" handle.',
      ),
    );
    return problems;
  }
  if (region.collectEdges.length > 1) {
    problems.push(
      problem(
        'map_multiple_collect_edges',
        `${carrierName} has ${region.collectEdges.length} collector edges; it collects from exactly one step.`,
        [region.carrierId, ...region.collectEdges.map(edgeSource)],
        'Keep one collector edge.',
      ),
    );
  }
  if (region.itemEdges.length > 1) {
    problems.push(
      problem(
        'map_multiple_item_edges',
        `${carrierName} sends each item to ${region.itemEdges.length} steps; a body has one first step.`,
        [region.carrierId, ...region.itemEdges.map(edgeTarget)],
        'Keep one "each item" edge, and feed the other steps from the first one.',
      ),
    );
  }
  if (!region.entryId) {
    problems.push(
      problem(
        'map_region_empty',
        `${carrierName} collects from ${name(region.exitId)} but does not say where each item goes.`,
        [region.carrierId],
        'Pick the first step in the panel, or drag from the Map\u2019s "each item" handle to it.',
      ),
    );
    return problems;
  }
  if (region.exitId === region.carrierId) {
    problems.push(
      problem(
        'map_region_empty',
        'The body is empty: the collector comes straight from the Map.',
        [region.carrierId],
        'Put at least one step (for example a Calculate) between "each item" and "collect".',
      ),
    );
    return problems;
  }

  const executionIds = new Set(
    nodes.filter(isExecutionNode).map((node) => node.id),
  );
  if (topologicalLevels(executionIds, edges) === null) {
    problems.push(
      problem(
        'map_unbounded_cycle',
        'The canvas has a cycle that is not the dashed collector edge.',
        [],
        'Only the collector may go back into the Map; remove any other edge that closes a loop.',
      ),
    );
    return problems;
  }
  if (
    !region.regionIds.includes(region.entryId) ||
    !region.regionIds.includes(region.exitId!)
  ) {
    problems.push(
      problem(
        'map_region_path_missing',
        `${name(region.entryId)} does not lead to ${name(region.exitId)}, so the body is empty.`,
        [region.entryId, region.exitId!],
        'Connect the first step (directly or through other steps) into the step whose output is collected.',
      ),
    );
    return problems;
  }

  const regionSet = new Set(region.regionIds);
  const levels = topologicalLevels(regionSet, edges) ?? [];
  if (levels[0]?.length !== 1 || levels[0][0] !== region.entryId) {
    const extras = (levels[0] ?? []).filter((id) => id !== region.entryId);
    problems.push(
      problem(
        'map_region_entry_ambiguous',
        `The body has more than one first step: ${[region.entryId, ...extras].map(name).join(', ')}.`,
        [region.entryId, ...extras],
        'Every step in the body must come after the first one; feed the others from it.',
      ),
    );
  }
  const last = levels[levels.length - 1] ?? [];
  if (last.length !== 1 || last[0] !== region.exitId) {
    const extras = last.filter((id) => id !== region.exitId);
    problems.push(
      problem(
        'map_region_exit_ambiguous',
        `The body has more than one last step: ${[region.exitId!, ...extras].map(name).join(', ')}.`,
        [region.exitId!, ...extras],
        'Everything in the body must flow into the step whose output is collected.',
      ),
    );
  }

  // Values in and out of the body.
  const ordinary = ordinaryEdges(edges, executionIds);
  const leavingBranches = branchEdgesLeavingRegion(edges, regionSet, byId);
  for (const edge of ordinary) {
    if (leavingBranches.has(edge.id)) continue;
    const from = edgeSource(edge);
    const to = edgeTarget(edge);
    if (
      regionSet.has(to) &&
      !regionSet.has(from) &&
      from !== region.carrierId
    ) {
      problems.push(
        problem(
          'map_region_external_input_ambiguous',
          `${name(from)} feeds ${name(to)}, which is inside the For each body.`,
          [from, to],
          `Outside values may enter the body only with the item. Move ${name(to)} out of the body, or pass the value into the list the Map reads.`,
        ),
      );
    }
    if (regionSet.has(from) && !regionSet.has(to) && to !== region.carrierId) {
      problems.push(
        problem(
          'map_region_external_output_ambiguous',
          `${name(from)} is inside the For each body but sends its output to ${name(to)} outside it.`,
          [from, to],
          `Only the collected step leaves the body. Read every item\u2019s result from ${carrierName} (Ordered Results) instead.`,
        ),
      );
    }
  }
  for (const edge of edges) {
    if (isForEachEdge(edge)) continue;
    const from = edgeSource(edge);
    const to = edgeTarget(edge);
    if (regionSet.has(to) && !executionIds.has(from) && byId.has(from)) {
      problems.push(
        problem(
          'map_region_handles_unsupported',
          `${name(to)} is inside the For each body and has ${name(from)} attached; tools, memory and notes cannot be used inside a body yet.`,
          [from, to],
          'Detach it, or move the node out of the body.',
        ),
      );
    }
  }

  // Every body node must be safe to run once per item.
  for (const id of region.orderedIds.length
    ? region.orderedIds
    : region.regionIds) {
    const s = struct(byId.get(id));
    if (!s) continue;
    const kind = s.display_name || s.node_id;
    if (isGatingNode(byId.get(id))) {
      if (!canBeInForEach(s, catalog)) {
        problems.push(
          problem(
            'map_region_node_unsafe',
            `${name(id)} (${kind}) cannot be inside the For each body in this deployment.`,
            [id],
            'Branches inside a body are switched off here; branch after the For each, or inside a Calculate formula with if().',
          ),
        );
      }
      continue;
    }
    if (!canBeInForEach(s, catalog)) {
      problems.push(
        problem(
          'map_region_node_unsafe',
          `${name(id)} (${kind}) cannot run once per item inside the For each body.`,
          [id],
          forEachAlternatives(catalog) || undefined,
        ),
      );
    } else if (id === region.entryId && !forEachItemSlot(s, catalog)) {
      problems.push(
        problem(
          'map_item_target_invalid',
          `${name(id)} has no input that can take the item.`,
          [id],
          'Start the body with a step that takes a value, for example a Calculate or a Lambda.',
        ),
      );
    }
  }

  // Phase 8d: branches inside the body rejoin before the collected step.
  problems.push(
    ...checkBranchesRejoin({
      kind: 'map',
      nodes,
      edges,
      regionIds: region.regionIds,
      entryId: region.entryId,
      exitId: region.exitId,
      name,
    }),
  );

  // History budget against the authored item cap.
  const carrierStruct = catalogStructFor(struct(region.carrier), catalog);
  const budget = historyBudgetFor(region, carrierStruct);
  const config: MapExecutionConfig | null = getMapConfig(
    struct(region.carrier),
  );
  const cap = config?.max_items ?? 64;
  if (cap > budget.fits) {
    problems.push(
      problem(
        'map_history_budget',
        `${budget.sentence} Max items is ${cap}, so a long list would be refused at Run.`,
        [region.carrierId],
        `Lower Max items to ${budget.fits}, or make the body shorter.`,
        'warning',
      ),
    );
  }
  if (config?.region != null) {
    problems.push(
      problem(
        'map_authored_runtime_state',
        'The For each settings contain server-compiled state that must not be saved.',
        [region.carrierId],
        'Re-save the body from the panel; it writes only the authored part.',
      ),
    );
  }
  const catalogMap = catalog?.find((entry) => entry.node_id === MAP_NODE_ID);
  if (catalogMap && !mapSupportsRegion(catalogMap)) {
    problems.push(
      problem(
        'map_region_runtime_unsupported',
        'This runtime runs a single step per item; multi-step For each bodies are not available yet.',
        [region.carrierId],
        'Pick one step in the panel instead, or wait for the runtime update.',
        'warning',
      ),
    );
  }
  return problems;
};
